/**
 * Flight block seat shortage on the website Tours (ADLC-1 slice 1) - QA doc docs features/flight-block-seat-shortage/README.md
 * R-SS5 (show only: badge, note, Other flights, Book disabled with the reason), R-SS6 (after the bookable rows - ordered by packages-api),
 * R-SS12 (EN + RU), R-SS14 (NoSeats 4 / SeatsOnRequest 5 next to the old OnRequest 0), error code FLIGHT_PRICE_OVERBOOKED /
 * FlightPriceNoMoreAvailable + NO_SEATS; online-api contract PackageFlightAvailability (spec REQ-3 .. REQ-7, AC-34 .. AC-47).
 *
 * The state is READ from what online-api sends - never computed here: a leg whose availability is NoSeats (or a value the site does not know,
 * fail-closed - contract: "a consumer that does not know a value must treat it as NOT bookable"), or `bookable: false`, makes the tour show-only.
 *
 * Slice 2b (R-SS7, built 30.10.2026, dormant until online-api sends it): a tour is ON REQUEST only when the flight says `bookable: true` AND at
 * least one leg is SeatsOnRequest (name or 5) AND every short leg is SeatsOnRequest (`isTourOnRequest`). Anything less - `bookable` absent or
 * false, a NoSeats or unknown leg next to it - stays show-only (fail closed: never sold on a guess). An on-request tour is bookable: grey badge
 * "Upon request", row note, Details warning, cart toast, and the checkout acceptance step (checkout.service.ts).
 */
import {
  CheckoutFormOut,
  FlightAvailabilityType,
  FlightSeatsWarning,
  PackageFlight,
  RefreshPriceResultEnum,
  TourPackage
} from '@appTypes/api.types';

/** The availability values this site knows and sells as before (absent = as before). */
const BOOKABLE_STATES: ReadonlySet<string> = new Set([
  FlightAvailabilityType.FreeSale,
  FlightAvailabilityType.FewPlaces,
  FlightAvailabilityType.OnRequest,
  FlightAvailabilityType.StopSale
]);

/** The reason packages-api / online-api names on a refused checkout of a show-only tour (CalcCheckout `noMoreAvailableReason`). */
export const NO_SEATS_REASON = 'NO_SEATS';
/** The code of a refused registration of a show-only tour (packages-api / reservations-api; 200 `error=true` or a 409 body). */
export const FLIGHT_PRICE_OVERBOOKED = 'FLIGHT_PRICE_OVERBOOKED';
/** The Details / checkout text of a show-only tour (code-style key: it carries a plural). */
export const TOUR_NO_SEATS_DETAILS_KEY = 'TOUR_NO_SEATS_DETAILS';

// #region slice 2b - on request (R-SS7, R-SS7a; error codes FLIGHT_SEATS_ON_REQUEST_NOT_ACCEPTED, FLIGHT_PRICE_OVERBOOKED + OVER_BLOCK_LIMIT)
/** CalcCheckout `flightSeatsWarning.code` of an on-request fare (packages-api, switch FlightSeats:OnRequestEnabled on). */
export const FLIGHT_SEATS_ON_REQUEST = 'FLIGHT_SEATS_ON_REQUEST';
/** ConfirmCheckout refusal: an on-request fare registered without `acceptSeatsOnRequest: true` - nothing registered. */
export const FLIGHT_SEATS_ON_REQUEST_NOT_ACCEPTED = 'FLIGHT_SEATS_ON_REQUEST_NOT_ACCEPTED';
/** `noMoreAvailableReason` of a fare whose limit over the block is reached (R-SS10) - shown as "no longer available on request". */
export const OVER_BLOCK_LIMIT_REASON = 'OVER_BLOCK_LIMIT';
/** The Details / checkout warning of an on-request tour (code-style key: it carries a plural). */
export const TOUR_ON_REQUEST_DETAILS_KEY = 'TOUR_ON_REQUEST_DETAILS';
/** The row note of an on-request tour (`tour-row-on-request-note`). */
export const ON_REQUEST_ROW_NOTE = '{flight}: seats on request - you can book, the operator confirms the seats.';
/** The toast after an on-request tour went into the cart (`tour-cart-added-on-request-toast`). */
export const ON_REQUEST_CART_TOAST = 'Added to the cart. Flight {flight}: on request.';
/** The required checkbox of the checkout acceptance step (`checkout-on-request-accept-label`). */
export const ON_REQUEST_ACCEPT_LABEL =
  'I understand that the seats of this flight are on request and are confirmed by the operator with the airline.';
/** Why Register is disabled (`checkout-on-request-register-reason`) and the text of a FLIGHT_SEATS_ON_REQUEST_NOT_ACCEPTED refusal. */
export const ON_REQUEST_NOT_ACCEPTED_TEXT = 'Confirm that you understand the flight is on request to continue.';
/** A FLIGHT_PRICE_OVERBOOKED (or OVER_BLOCK_LIMIT) on an on-request cart: the seats over the block are gone. */
export const ON_REQUEST_NO_LONGER_TEXT =
  'Seats on this flight are no longer available on request. Choose another flight.';
// #endregion

export type FlightLeg = 'departure' | 'return';

/** A leg is short when online-api says NoSeats / SeatsOnRequest, or sends a value this site does not know (fail-closed). */
export const isLegShort = (type: FlightAvailabilityType | string | number | null | undefined): boolean => {
  if (type === null || type === undefined || type === '') return false;
  if (typeof type === 'number') return type === 4 || type === 5 || ![-1, 0, 1, 3].includes(type);
  return !BOOKABLE_STATES.has(type);
};

/** The legs of the tour's flight that are short for the party, departure first. */
export const shortLegs = (flight: PackageFlight | null | undefined): FlightLeg[] => {
  if (!flight) return [];
  const legs: FlightLeg[] = [];
  if (isLegShort(flight.departureAvailabilityType)) legs.push('departure');
  if (isLegShort(flight.returnAvailabilityType)) legs.push('return');
  return legs;
};

/** The availability online-api sent for one leg. */
const legType = (flight: PackageFlight | null | undefined, leg: FlightLeg) =>
  leg === 'departure' ? flight?.departureAvailabilityType : flight?.returnAvailabilityType;

/** R-SS14: the leg is short but bookable over the block on request - SeatsOnRequest by name or 5; nothing else. */
export const isLegOnRequest = (type: FlightAvailabilityType | string | number | null | undefined): boolean =>
  type === FlightAvailabilityType.SeatsOnRequest || type === 5;

/**
 * R-SS7 (fail closed): the tour is sold on request only when online-api says `bookable: true` (exactly - absent, null or false is not enough),
 * at least one leg is SeatsOnRequest and every short leg is SeatsOnRequest. A NoSeats or unknown leg next to it keeps the tour show-only.
 */
export const isTourOnRequest = (pkg: Pick<TourPackage, 'flight'> | null | undefined): boolean => {
  const flight = pkg?.flight;
  if (!flight || flight.bookable !== true) return false;
  const short = shortLegs(flight);
  return short.length > 0 && short.every((leg) => isLegOnRequest(legType(flight, leg)));
};

/** The tour cannot be booked with its flight: a short leg or `bookable: false` (R-SS5, AC-35) - unless it is sold on request (R-SS7). */
export const isTourShowOnly = (pkg: Pick<TourPackage, 'flight'> | null | undefined): boolean =>
  !!pkg?.flight && !isTourOnRequest(pkg) && (pkg.flight.bookable === false || shortLegs(pkg.flight).length > 0);

/** The seat badge of one leg: 'on-request' (grey "Upon request"), 'no-seats' (red "Not enough seats") or null (the class badge as today). */
export type LegSeatMark = 'on-request' | 'no-seats' | null;

/** R-SS5 / R-SS7: a short leg of an on-request tour is 'on-request'; any other short leg (or one this site does not know) 'no-seats'. */
export const legSeatMark = (flight: PackageFlight | null | undefined, leg: FlightLeg): LegSeatMark => {
  const type = legType(flight, leg);
  if (!isLegShort(type)) return null;
  return isLegOnRequest(type) && isTourOnRequest({ flight: flight ?? undefined }) ? 'on-request' : 'no-seats';
};

/** The plane icon class of a short leg: grey `on_request` (prototype) or red `no_seats`; null = the leg's own state class as today. */
const PLANE_CLASS: Record<NonNullable<LegSeatMark>, string> = { 'on-request': 'on_request', 'no-seats': 'no_seats' };
export const legPlaneClass = (flight: PackageFlight | null | undefined, leg: FlightLeg): string | null => {
  const mark = legSeatMark(flight, leg);
  return mark ? PLANE_CLASS[mark] : null;
};

/** The flight code of one leg as the row shows it ("SU9111"). */
export const legFlightCode = (flight: PackageFlight | null | undefined, leg: FlightLeg): string =>
  ((leg === 'departure' ? flight?.departureSegments?.[0] : flight?.returnSegments?.[0])?.flightCode ?? '').trim();

/** `{flight}` of the texts: the short leg(s) ("SU9111", or "SU9111 / SU9112" when both are short); the departure when no leg is named. */
export const shortFlightLabel = (flight: PackageFlight | null | undefined): string => {
  const legs = shortLegs(flight);
  const codes = (legs.length ? legs : (['departure'] as FlightLeg[]))
    .map((leg) => legFlightCode(flight, leg))
    .filter(Boolean);
  return codes.join(' / ');
};

/** `{n}` of the texts: the seats the party needs as the flight states them, else adults + children of the stay. */
export const seatsNeededOf = (pkg: Pick<TourPackage, 'flight' | 'hotel'> | null | undefined): number => {
  const stated = pkg?.flight?.seatsNeeded;
  if (typeof stated === 'number' && stated > 0) return stated;
  return (pkg?.hotel?.adults ?? 0) + (pkg?.hotel?.children ?? 0);
};

/** The parameters of TOUR_NO_SEATS_DETAILS for a tour. */
export const noSeatsTextParams = (pkg: Pick<TourPackage, 'flight' | 'hotel'> | null | undefined) => ({
  flight: shortFlightLabel(pkg?.flight),
  n: seatsNeededOf(pkg)
});

/**
 * A checkout answer refusing a show-only tour: CalcCheckout `FlightPriceNoMoreAvailable` + NO_SEATS (or OVER_BLOCK_LIMIT - the limit over the
 * block is reached, R-SS7a), or a registration FLIGHT_PRICE_OVERBOOKED.
 */
export const isNoSeatsRefusal = (
  response: Pick<CheckoutFormOut, 'refreshPriceResult' | 'noMoreAvailableReason' | 'errorCode'> | null | undefined
): boolean =>
  !!response &&
  ((response.refreshPriceResult === RefreshPriceResultEnum.FlightPriceNoMoreAvailable &&
    (response.noMoreAvailableReason === NO_SEATS_REASON ||
      response.noMoreAvailableReason === OVER_BLOCK_LIMIT_REASON)) ||
    response.errorCode === FLIGHT_PRICE_OVERBOOKED);

/**
 * website-web#172 (R-SS5a): packages-api writes reservationId on every checkout answer, a NO_SEATS refusal too - a refused price
 * (FlightPriceNoMoreAvailable + the refusal) registered nothing, whatever reservationId it echoes.
 */
export const isRegisteredAnswer = (
  response:
    | Pick<CheckoutFormOut, 'reservationId' | 'refreshPriceResult' | 'noMoreAvailableReason' | 'errorCode'>
    | null
    | undefined
): boolean =>
  !!response?.reservationId &&
  !(response.refreshPriceResult === RefreshPriceResultEnum.FlightPriceNoMoreAvailable && isNoSeatsRefusal(response));

/** The tour of the cart the refusal is about: the first show-only tour, else the first tour (the cart was built before the block sold out). */
export const refusedTour = (tours: (TourPackage | null | undefined)[]): TourPackage | null =>
  tours.find((t) => !!t && isTourShowOnly(t)) ?? tours.find((t) => !!t?.flight) ?? null;

// #region slice 2b - on request (R-SS7)

/** The on-request legs of a flight, departure first. */
export const onRequestLegs = (flight: PackageFlight | null | undefined): FlightLeg[] =>
  isTourOnRequest({ flight: flight ?? undefined }) ? shortLegs(flight) : [];

/** `{flight}` of the on-request texts: the on-request leg(s) ("SU9111", "SU9111 / SU9112"); else the short / departure leg as the no-seats texts. */
export const onRequestFlightLabel = (flight: PackageFlight | null | undefined): string => {
  const codes = onRequestLegs(flight)
    .map((leg) => legFlightCode(flight, leg))
    .filter(Boolean);
  return codes.length ? codes.join(' / ') : shortFlightLabel(flight);
};

/** A whole number of seats as the texts show it (never negative); null when not stated. */
const seatCount = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : null;

/** The parameters of TOUR_ON_REQUEST_DETAILS and the row note / toast: `{flight}`, `{free}` (seatsLeft, else 0) and `{n}` (seats needed). */
export const onRequestTextParams = (pkg: Pick<TourPackage, 'flight' | 'hotel'> | null | undefined) => ({
  flight: onRequestFlightLabel(pkg?.flight),
  free: seatCount(pkg?.flight?.seatsLeft) ?? 0,
  n: seatsNeededOf(pkg)
});

/** The same parameters with a CalcCheckout warning: its live seat figures win over the cart's (the answer is newer). */
export const onRequestWarningTextParams = (
  pkg: Pick<TourPackage, 'flight' | 'hotel'> | null | undefined,
  warning: FlightSeatsWarning | null | undefined
) => {
  const base = onRequestTextParams(pkg);
  const needed = seatCount(warning?.seatsNeeded);
  return {
    flight: base.flight,
    free: seatCount(warning?.seatsLeft) ?? base.free,
    // a party of 0 is no party: the cart's figure stays
    n: needed !== null && needed > 0 ? needed : base.n
  };
};

/** R-SS7a: CalcCheckout says the cart holds an on-request fare (code FLIGHT_SEATS_ON_REQUEST); any other code or none = no step. */
export const hasOnRequestWarning = (
  response: Pick<CheckoutFormOut, 'flightSeatsWarning'> | null | undefined
): boolean => response?.flightSeatsWarning?.code === FLIGHT_SEATS_ON_REQUEST;

/** R-SS7a: ConfirmCheckout refused the on-request fare because the warning was not accepted - nothing was registered. */
export const isOnRequestNotAccepted = (response: Pick<CheckoutFormOut, 'errorCode'> | null | undefined): boolean =>
  response?.errorCode === FLIGHT_SEATS_ON_REQUEST_NOT_ACCEPTED;

// #endregion
